import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { useGuestSiteController } from './GuestSite.controller';
import { AnalyticsContext, useLocation } from '@shared/core';
import { BrannanNavigation, DesignLayoutType } from '@graphql/generated';
import { ScrollToProvider } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/providers/ScrollToProvider/ScrollToProvider';
import type { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import type { StaticContext } from '@react-router';
import { Box, useDisclosure } from '@withjoy/joykit';
import JustFonts from '@apps/guest/packages/layout-engine/components/JustFonts';
import { GuestSiteDebugger } from '@apps/guest/components/GuestSiteDebugger';
import { ActionBar } from '@apps/guest/packages/layout-engine/components/ActionBar';
import { UnlockDialogProvider } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialogProvider';
import { LayoutProvider } from '@shared/context/LayoutProvider';
import { wrapWithGuestSiteHealthProvider } from './GuestSite.health';
import { GuestSiteFeatureSet } from '@apps/guest/packages/layout-engine/components/GuestSiteFeatureSet/GuestSiteFeatureSet';
import { useNativeState } from '@shared/core/native';
import { useInformParentOnMousedown } from './hooks/useInformParentOnMousedown';
import { EventDesignThemingProvider } from '@shared/core/website/theming';
import { EventDesignJoyKitThemeOverridesProvider } from '@shared/core/website/theming/EventDesignJoyKitThemeOverridesProvider';
import { ColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { SmartAppBanner, SmartAppBannerProvider, useSmartAppBannerProvider } from '@shared/components/SmartAppBanner';
import { usePurchaseContextIdFromLocation } from '@apps/registry/common/utils/dropship';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { DiscountsProvider } from '@shared/components/DiscountsProvider/DiscountsProvider';
import { HotelsDiscountsProvider } from '@shared/components/DiscountsProvider/hotels/HotelsDiscountsProvider';
import { UnpublishedWebsite } from './components/UnpublishedWebsite/UnpublishedWebsite';
import { StickyNavBarOffsetProvider } from '@apps/guest/packages/layout-engine/components/StickyNavBarOffset';
import { WebsiteBannerProvider, useWebsiteBannerOffset } from '@shared/components/WebsiteBanner';
import { PremiumHeaderLayoutContext } from './hooks/usePremiumHeaderLayout';
import { HeaderLayoutUpgradeDialog } from '@shared/components/HeaderLayoutUpgradeDialog';
import { useGuestSiteTelemetry } from '@apps/guest/GuestSite.telemetry';
import { GUEST_SITE_START_PORTAL_ID } from './GuestSite.constants';

import { LazyStatusBanner } from './components/StatusBanner/StatusBanner.lazy';
import { LazyLayoutAloha } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/LayoutAloha.lazy';
import { LazyLayoutBrannan } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/LayoutBrannan.lazy';
import { LazyLayoutBrannanSinglePage } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/LayoutBrannanSinglePage.lazy';
import { LazyPreviewListener } from './PreviewListener.lazy';
import { LazyAnnouncement } from '@apps/guest/packages/layout-engine/components/Announcement/Announcement.lazy';
import { LazyPurchaseConfirmationDialog } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Accommodations/components/PurchaseConfirmationDialog/PurchaseConfirmationDialog.lazy';
import { RichTextStylesProvider } from '@apps/guest/packages/layout-engine/widgets/Custom/components/RichText/RichTextStylesProvider';
import { usePreloadTranslationNamespaces } from '@shared/hooks/usePreloadTranslationNamespaces';

export interface GuestSiteLayoutProps
  extends Readonly<{
    eventHandle: string;
    event: GuestSiteEvent;
    layout: DesignLayoutType;
    setLoadingTheme?: Maybe<Dispatch<SetStateAction<boolean>>>;
    setIsLastPageVisible?: (value: boolean) => void;
    setIsPastFirstPage?: (value: boolean) => void;
    isNativeAccommodationPage: boolean;
    isNativeBrannanOverridePage: boolean;
  }> {}

const GuestSiteLayout: React.FC<GuestSiteLayoutProps> = ({
  eventHandle,
  event,
  layout,
  setLoadingTheme,
  setIsLastPageVisible,
  setIsPastFirstPage,
  isNativeBrannanOverridePage,
}) => {
  const brannanNavigation = event.eventDesign?.brannanNavigation;
  const [isSimpleScrollLayoutEnabled] = useIsFeatureEnabled('websiteSimpleScrollLayoutEnabled');

  const layoutSwitch = () => {
    const layoutTypes = {
      [DesignLayoutType.aloha]: LazyLayoutAloha,
      [DesignLayoutType.brannan]:
        !isNativeBrannanOverridePage && brannanNavigation === BrannanNavigation.singlePage && isSimpleScrollLayoutEnabled ? LazyLayoutBrannanSinglePage : LazyLayoutBrannan,
    };
    const Layout = layoutTypes[layout];

    return (
      (event && (
        <Layout
          setLoadingTheme={setLoadingTheme}
          setIsLastPageVisible={setIsLastPageVisible}
          setIsPastFirstPage={setIsPastFirstPage}
          eventHandle={eventHandle}
          event={event}
        />
      )) ||
      null
    );
  };

  return layoutSwitch();
};

export interface GuestSiteBodyProps
  extends Readonly<{
    eventHandle: string;
    staticContext: StaticContext | undefined;
  }> {}

const GuestSiteBodyBase: React.FC<GuestSiteBodyProps> = wrapWithGuestSiteHealthProvider(({ eventHandle, staticContext }) => {
  const {
    event,
    layout,
    eventPageRoutes,
    isPreviewing,
    handleOnPreviewUpdate,
    handleOnRefetchData,
    loading,
    justFonts,
    graphicAccent,
    shouldRenderActionBar,
    setLoadingTheme,
    setIsPastFirstPage,
    showAnnouncement,
    onAnnouncementDismiss,
    isNativeAccommodationPage,
    isNativeBrannanOverridePage,
    showUnpublishedPage,
    isLoadingWebsitePublishCapability,
    premiumHeaderLayout,
  } = useGuestSiteController({
    eventHandle,
    staticContext,
  });
  const { isNative } = useNativeState();

  usePreloadTranslationNamespaces(
    'joykit',
    'guestKit',
    'layoutEngine',
    'headerLayoutUpgrade',
    'sharedComponents',
    'countries',
    'guestSiteCustom',
    'travelMap',
    'accommodationsGuest',
    'hotelTile',
    'guestSiteTravel',
    'dates',
    'accommodationsConsole',
    'websitePages',
    'publishReminder',
    'eventSiteApps',
    'guestRegistry',
  );

  const { smartAppBannerHeight } = useSmartAppBannerProvider();

  const { pathname } = useLocation();
  const purchaseId = usePurchaseContextIdFromLocation();
  const updateSearchParam = useUpdateSearchParam();
  const analytics = useContext(AnalyticsContext);
  const [isPurchaseConfirmationOpen, setIsPurchaseConfirmationOpen] = useState(false);
  const { isOpen: isPurchaseConfirmationDialogOpen, onClose: onPurchaseConfirmationClose } = useDisclosure({
    isOpen: isPurchaseConfirmationOpen,
    onClose: () => {
      updateSearchParam('pcId', null);
      setIsPurchaseConfirmationOpen(false);
    },
  });
  const guestSiteTelemetry = useGuestSiteTelemetry();

  // Get website banner offset
  const { bannerOffset } = useWebsiteBannerOffset();

  const isTravelOrAccomPage = useMemo(() => pathname.includes('/travel') || pathname.includes('/accommodation'), [pathname]);
  const shouldRenderPurchaseConfirmationDialog = !!purchaseId && isTravelOrAccomPage;

  useEffect(() => {
    if (shouldRenderPurchaseConfirmationDialog) {
      setIsPurchaseConfirmationOpen(true);
      analytics.track({
        category: 'guest.purchase',
        action: 'PageViewed',
        extraInfo: { name: 'registryPurchaseConfirmation', pageType: pathname.includes('/travel') ? 'travel' : 'accommodation' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRenderPurchaseConfirmationDialog]);

  // hook to inform irame parent on mousedown
  useInformParentOnMousedown(isPreviewing);

  // Use website banner offset (no need for useBannersOffset anymore)
  const totalOffset = Math.max(smartAppBannerHeight, bannerOffset);

  return (
    <>
      <Box paddingTop={totalOffset}>
        {!loading && <LazyStatusBanner />}
        <GuestSiteFeatureSet loadingGuestSite={loading || isLoadingWebsitePublishCapability}>
          {event && showUnpublishedPage ? (
            <UnpublishedWebsite />
          ) : event && layout ? (
            <LayoutProvider
              layout={layout}
              brannanNavigation={event.eventDesign?.brannanNavigation}
            >
              <EventDesignThemingProvider eventDesign={event.eventDesign}>
                <UnlockDialogProvider>
                  <ColorPaletteProvider eventDesign={event.eventDesign}>
                    <EventDesignJoyKitThemeOverridesProvider>
                      <RichTextStylesProvider>
                        {showAnnouncement && !isNative && (
                          <LazyAnnouncement
                            onAnnouncementClosed={() => onAnnouncementDismiss(event.info.announcementBanner.title)}
                            content={<>{event.info.announcementBanner.message}</>}
                            title={event.info.announcementBanner.title}
                            graphicAccent={graphicAccent}
                          />
                        )}
                        {justFonts ? (
                          <JustFonts />
                        ) : (
                          <ScrollToProvider>
                            {isPreviewing && (
                              <LazyPreviewListener
                                eventHandle={eventHandle}
                                eventPageRoutes={eventPageRoutes}
                                eventDesign={event?.eventDesign}
                                onRefetchData={handleOnRefetchData}
                                onUpdate={handleOnPreviewUpdate}
                              />
                            )}
                            <PremiumHeaderLayoutContext.Provider value={premiumHeaderLayout}>
                              <StickyNavBarOffsetProvider offset={bannerOffset}>
                                <div id={GUEST_SITE_START_PORTAL_ID} />
                                <GuestSiteLayout
                                  setLoadingTheme={setLoadingTheme}
                                  eventHandle={eventHandle}
                                  event={event}
                                  layout={layout}
                                  setIsPastFirstPage={setIsPastFirstPage}
                                  isNativeAccommodationPage={isNativeAccommodationPage}
                                  isNativeBrannanOverridePage={isNativeBrannanOverridePage}
                                />
                              </StickyNavBarOffsetProvider>
                            </PremiumHeaderLayoutContext.Provider>
                          </ScrollToProvider>
                        )}
                        <HeaderLayoutUpgradeDialog
                          isOpen={premiumHeaderLayout.dialog.isOpen}
                          onClose={premiumHeaderLayout.dialog.onClose}
                          secondaryAction={premiumHeaderLayout.dialog.secondaryAction}
                          onDialogOpen={() => guestSiteTelemetry.headerLayoutUpgradeDialog.enter()}
                          onUpgradeClick={() => guestSiteTelemetry.headerLayoutUpgradeDialogUpgradeClicked()}
                          onNoThanksClick={() => guestSiteTelemetry.headerLayoutUpgradeDialogNoThanksClicked()}
                          onAllDoneClick={() => guestSiteTelemetry.headerLayoutUpgradeDialogAllDoneClicked()}
                        />
                      </RichTextStylesProvider>
                    </EventDesignJoyKitThemeOverridesProvider>
                    <GuestSiteDebugger event={event} />
                  </ColorPaletteProvider>
                  {shouldRenderActionBar && <ActionBar eventId={event.id} />}
                </UnlockDialogProvider>
              </EventDesignThemingProvider>
            </LayoutProvider>
          ) : null}
        </GuestSiteFeatureSet>
      </Box>
      <SmartAppBanner telemetryCategory="wedding" />
      {/* Defer rendering the purchase confirmation dialog because it imports from registry app */}
      {shouldRenderPurchaseConfirmationDialog && (
        <LazyPurchaseConfirmationDialog
          isOpen={isPurchaseConfirmationDialogOpen}
          onClose={onPurchaseConfirmationClose}
        />
      )}
    </>
  );
});

const GuestSiteBody: React.FC<GuestSiteBodyProps> = props => {
  return (
    <DiscountsProvider>
      <HotelsDiscountsProvider>
        <SmartAppBannerProvider>
          <WebsiteBannerProvider>
            <GuestSiteBodyBase {...props} />
          </WebsiteBannerProvider>
        </SmartAppBannerProvider>
      </HotelsDiscountsProvider>
    </DiscountsProvider>
  );
};

GuestSiteBody.displayName = 'GuestSiteBody';

export default GuestSiteBody;
